/**
 * 生产环境二次确认 —— 方案 §5.3 / 对接文档 §5.2（`07020041` 的正确处理姿势）
 *
 * 本组件只负责「把影响说清楚 + 让用户显式勾选」；**令牌怎么拿、什么时候重发都由
 * `hooks/useProdConfirm.js` 一个地方管**（硬编码占位令牌曾导致生产全量不可用，见那里的注释）。
 *
 * 什么时候弹：
 *  - 新建发布选 master：点【确定】就先弹（`confirmFirst`），用户确认后才去签 token；
 *  - 详情页停止/重发：撞 `07020041` 才弹，且 `prodEnv=false` 时不弹（那是 G2 环境不一致）。
 *
 * 🔴 两条硬规矩
 *  1. **没有「发布描述」输入框**（PRD rev 965 已删字段，别照旧稿加回来）。
 *  2. 没勾【我已确认上述影响】时，确认按钮**置灰**；关闭/取消不回退任何状态，
 *     弹窗（PublishModal）**不关**、requestId 不变 —— 用户改个勾再点确定，还是同一个请求。
 */
/* eslint react/prop-types: "off" -- 本模块用 JSDoc 声明 props，不额外引 prop-types */
import React, { useEffect, useState } from 'react';
import { Alert, Button, Checkbox, Modal, Typography } from 'antd';
import { BTN, TIP } from '../../constants';
import styles from './prodConfirm.less';

const { Text } = Typography;

export default function ProdConfirmModal({
  visible,
  title,
  services = [],
  /** 「上述影响」具体是什么（停止类写清状态怎么变，重发类用 TIP.PROD_REPUBLISH） */
  message = '',
  /** 已经失败过一次时的原因（如 token 过期/已用过）：只说事实，不重复弹同一个框就完事 */
  note = '',
  loading = false,
  onConfirm,
  onCancel,
}) {
  // 每次打开都从「未勾选」开始：确认必须是一次一次点出来的，不许记住上次的勾（AC-12）
  const [checked, setChecked] = useState(false);
  useEffect(() => {
    if (visible) setChecked(false);
  }, [visible]);

  return (
    <Modal
      /* 🔴 默认文案是「发布」（新建发布用），详情页 4 个写操作传自己的标题（停止/重发影响完全不同） */
      title={title || TIP.PROD_CONFIRM}
      open={visible}
      onCancel={onCancel}
      width={520}
      maskClosable={false}
      destroyOnClose
      footer={[
        <Button key="cancel" onClick={onCancel}>
          {BTN.CANCEL}
        </Button>,
        <Button
          key="ok"
          danger
          type="primary"
          disabled={!checked}
          loading={loading}
          onClick={() => onConfirm && onConfirm()}
        >
          {BTN.CONFIRM_PROD}
        </Button>,
      ]}
    >
      {note ? (
        <Alert
          type="warning"
          showIcon
          message={note}
          className={styles.expireBar}
        />
      ) : null}

      {/* 新建发布会列本次要发的服务（得让用户看清范围）；
          详情页四个写操作针对的是单个批次/服务，由标题说清动作，不重复列清单。
          有依赖的服务在名字后面带上「（依赖 a、b）」——确认的就是这份真实提交载荷里的依赖关系；
          没有依赖就不加括号（不显示空壳） */}
      {services.length > 0 && (
        <div className={styles.summary}>
          <Text>
            共 <Text strong>{services.length}</Text> 个服务：
          </Text>
          <div>
            {services.map((s) => {
              const deps = s.dependsOn || [];
              return (
                <Text key={s.appName} code style={{ marginRight: 8 }}>
                  {s.appName}
                  {deps.length > 0 && `（依赖 ${deps.join('、')}）`}
                </Text>
              );
            })}
          </div>
        </div>
      )}

      {message ? (
        <Alert
          type="warning"
          showIcon
          message={message}
          className={styles.expireBar}
        />
      ) : null}

      <Checkbox
        className={styles.confirmCheck}
        checked={checked}
        onChange={(e) => setChecked(e.target.checked)}
      >
        {TIP.PROD_CONFIRM_CHECK}
      </Checkbox>
    </Modal>
  );
}
